import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { productivityAPI } from '@/services/productivity';
import { Calendar, TrendingUp, CheckCircle2, Clock, Trophy, X, User } from 'lucide-react';
import { cn } from '@/lib/utils';

const WeeklyViewPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // ✅ Modal state
  const [selectedDate, setSelectedDate] = useState(null);
  const [dailyTasks, setDailyTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);

  // ✅ Load Weekly Stats from API
  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await productivityAPI.getWeeklyStats(user.id);
        if (response.success) {
          setStats(response.data);
        }
      } catch (error) {
        console.error('Failed to load weekly stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [user.id]);

  // ✅ Load Daily Tasks when date is selected
  const loadDailyTasks = async (date) => {
    setLoadingTasks(true);
    try {
      const response = await productivityAPI.getDailyTasks(date);
      if (response.success) {
        setDailyTasks(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load daily tasks:', error);
      setDailyTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  // ✅ Handle day click
  const handleDayClick = (date, completedCount) => {
    if (completedCount > 0) {
      setSelectedDate(date);
      loadDailyTasks(date);
    }
  };

  // ✅ Close modal
  const closeModal = () => {
    setSelectedDate(null);
    setDailyTasks([]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-safety-orange"></div>
      </div>
    );
  }

  if (!stats) return <div>No data available</div>;

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <Helmet><title>Weekly View - Productivity</title></Helmet>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-navy-steal">Weekly Overview</h1>
          <p className="text-stone-500">Your productivity breakdown for the last 7 days</p>
        </div>
        <div className="bg-white px-4 py-2 rounded-lg border border-stone-200 shadow-sm flex items-center gap-3">
           <Trophy className="w-5 h-5 text-yellow-500" />
           <div>
             <p className="text-xs text-stone-500 uppercase">Most Productive Day</p>
             <p className="font-bold text-navy-steal">{stats.mostProductiveDay}</p>
           </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-7 gap-4">
        {stats.dailyData.map((day, idx) => {
          const isToday = day.date === today;
          const hasCompletedTasks = day.completedTasks > 0;
          
          return (
            <Card 
              key={idx} 
              className={cn(
                "border-t-4 shadow-sm transition-all",
                isToday ? "border-t-safety-orange bg-orange-50/20" : "border-t-stone-200",
                hasCompletedTasks && "cursor-pointer hover:shadow-md hover:scale-105"
              )}
              onClick={() => handleDayClick(day.date, day.completedTasks)}
            >
              <CardContent className="p-4 flex flex-col gap-3 h-full justify-between">
                <div className="text-center pb-2 border-b border-stone-100">
                  <p className="text-sm font-semibold text-stone-500">{day.dayName}</p>
                  <p className="text-xs text-stone-400">{new Date(day.date).getDate()}</p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-stone-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Focus
                    </span>
                    <span className="font-bold text-navy-steal">{day.focusMinutes}m</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-stone-500 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Tasks
                    </span>
                    <span className="font-bold text-navy-steal">{day.completedTasks}</span>
                  </div>
                </div>

                {/* Mini Bar representing focus intensity */}
                <div className="h-1.5 w-full bg-stone-100 rounded-full overflow-hidden mt-2">
                  <div 
                    className="h-full bg-blue-500 rounded-full transition-all" 
                    style={{ width: `${Math.min((day.focusMinutes / 300) * 100, 100)}%` }}
                  />
                </div>
                
                {hasCompletedTasks && (
                  <p className="text-xs text-center text-blue-600 font-medium mt-1">
                    Click to view tasks
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
           <CardTitle className="flex items-center gap-2">
             <TrendingUp className="w-5 h-5 text-safety-orange" />
             Weekly Summary
           </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-8 p-6">
           <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-full">
                <Clock className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Total Focus Time</p>
                <p className="text-3xl font-bold text-navy-steal">
                  {Math.floor(stats.totalFocus / 60)}<span className="text-lg text-stone-400">h</span> {stats.totalFocus % 60}<span className="text-lg text-stone-400">m</span>
                </p>
              </div>
           </div>

           <div className="flex items-center gap-4">
              <div className="p-3 bg-green-50 rounded-full">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Tasks Completed</p>
                <p className="text-3xl font-bold text-navy-steal">{stats.totalCompleted}</p>
              </div>
           </div>

           <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-50 rounded-full">
                <Calendar className="w-8 h-8 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Daily Average</p>
                <p className="text-3xl font-bold text-navy-steal">{Math.round(stats.totalFocus / 7)} <span className="text-lg text-stone-400">min/day</span></p>
              </div>
           </div>
        </CardContent>
      </Card>

      {/* ✅ Modal for Daily Tasks */}
      {selectedDate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden animate-in zoom-in-95 duration-200">
            <CardHeader className="bg-slate-50 border-b border-stone-200 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-xl">
                  Tasks Completed on {new Date(selectedDate).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </CardTitle>
                <p className="text-sm text-stone-500 mt-1">
                  {dailyTasks.length} task{dailyTasks.length !== 1 ? 's' : ''} completed
                </p>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={closeModal}
                className="rounded-full"
              >
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            
            <CardContent className="p-0 overflow-y-auto max-h-[60vh]">
              {loadingTasks ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-safety-orange"></div>
                </div>
              ) : dailyTasks.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-stone-400">
                  <Calendar className="w-12 h-12 mb-2 opacity-20" />
                  <p>No tasks found for this date</p>
                </div>
              ) : (
                <ul className="divide-y divide-stone-100">
                  {dailyTasks.map(task => (
                    <li key={task.id} className="p-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-navy-steal mb-1">
                            {task.title}
                          </h4>
                          
                          {/* ✅ Show who completed and who assigned */}
                          <div className="flex flex-wrap gap-2 mt-2">
                            {task.assigned_to_name && (
                              <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center gap-1">
                                <User className="w-3 h-3" />
                                Completed by: {task.assigned_to_name}
                              </span>
                            )}
                            {task.assigned_by_name && task.assigned_by !== task.user_id && (
                              <span className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded-full flex items-center gap-1">
                                <User className="w-3 h-3" />
                                Assigned by: {task.assigned_by_name}
                              </span>
                            )}
                          </div>
                          
                          <p className="text-xs text-stone-400 mt-2">
                            Completed at {new Date(task.completed_at).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default WeeklyViewPage;
